import React, { useState } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Tag,
  Image,
  Space,
  Select,
  Modal,
  message,
} from 'antd';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTable } from '@/hooks/useTable';
import {
  getArtistSpaceArtworksById,
  updateArtistSpaceArtwork,
} from '@/api/artistSpace';
import type {
  ArtistSpaceArtworksParams,
  ArtistSpaceArtwork,
} from '@/api/artistSpace';
import { PageLink } from '@/config/pageConfig';
import { FORMAT_TIME } from '@/types/common';
import dayjs from 'dayjs';

const STATUS_MAP: Record<number, { text: string; color: string }> = {
  1: { text: '发布成功', color: 'success' },
  2: { text: '审核未通过', color: 'default' },
};


const CommunityTopicSubmissionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const artistSpaceId = id ? Number(id) : undefined;
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const routeState = location.state as {
    topicTitle?: string;
    fromListPage?: number;
    fromListPageSize?: number;
  } | null;
  const topicTitle = routeState?.topicTitle ?? '';
  const [searchParams, setSearchParams] = useState<
    Omit<ArtistSpaceArtworksParams, 'artistSpaceId' | 'currentPage' | 'pageSize'>
  >({
    currentPage: 1,
    pageSize: 10,
  });
  const [takedownModal, setTakedownModal] = useState<{
    visible: boolean;
    record: ArtistSpaceArtwork | null;
  }>({ visible: false, record: null });

  const {
    data,
    isLoading,
    handleTableChange,
    updateParams,
    currentPage,
    pageSize,
    total,
    reload,
  } = useTable<ArtistSpaceArtwork, ArtistSpaceArtworksParams>({
    queryKey: 'community-topic-artworks',
    queryFn: async (current, size, params) => {
      if (!artistSpaceId || !params?.artistSpaceId) {
        return { total: 0, currentPage: current, pageSize: size, records: [] };
      }
      const res = await getArtistSpaceArtworksById({
        ...params,
        artistSpaceId,
        currentPage: current,
        pageSize: size,
      });
      return {
        total: res.total ?? 0,
        currentPage: res.currentPage ?? current,
        pageSize: res.pageSize ?? size,
        records: res.records ?? [],
      };
    },
    defaultParams: {
      ...searchParams,
      artistSpaceId: artistSpaceId ?? 0,
    },
  });

  const handleSearch = async () => {
    const values = await form.validateFields().catch(() => ({}));
    const next = {
      ...searchParams,
      ...values,
      currentPage: 1,
      pageSize,
    };
    setSearchParams(next);
    updateParams({ ...next, artistSpaceId: artistSpaceId ?? 0 });
  };

  const handleReset = () => {
    form.resetFields();
    const next = { currentPage: 1, pageSize: 10 };
    setSearchParams(next);
    updateParams({ ...next, artistSpaceId: artistSpaceId ?? 0 });
  };

  const handleTakedownClick = (record: ArtistSpaceArtwork) => {
    setTakedownModal({ visible: true, record });
  };

  const handleTakedownConfirm = async () => {
    const record = takedownModal.record;
    const artworkId = record?.artworkId;
    const userId = record?.userId;
    if (artworkId == null || artworkId === '' || userId == null || userId === '') return;
    try {
      await updateArtistSpaceArtwork({
        artworkId,
        userId,
        status: 2, // 审核未通过
      });
      message.success('下架成功');
      setTakedownModal({ visible: false, record: null });
      reload();
    } catch (e) {
      message.error((e as Error).message || '下架失败');
    }
  };

  const handleRelistClick = async (record: ArtistSpaceArtwork) => {
    const artworkId = record?.artworkId;
    const userId = record?.userId;
    if (artworkId == null || artworkId === '' || userId == null || userId === '') return;
    try {
      await updateArtistSpaceArtwork({
        artworkId,
        userId,
        status: 1, // 发布成功
      });
      message.success('重新上架成功');
      reload();
    } catch (e) {
      message.error((e as Error).message || '上架失败');
    }
  };

  const columns = [
    {
      title: '序号',
      key: 'index',
      width: 70,
      render: (_: unknown, __: ArtistSpaceArtwork, index: number) =>
        (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: '投稿作品',
      dataIndex: 'artworkUrl',
      key: 'artworkUrl',
      width: 100,
      render: (url: string | null | undefined) =>
        url ? (
          <Image
            src={url}
            alt="作品"
            style={{ width: 64, height: 64, objectFit: 'cover' }}
          />
        ) : (
          <span style={{ color: '#999' }}>暂无</span>
        ),
    },
    {
      title: '作品文案',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (v: string) => v || '-',
    },
    {
      title: '投稿者昵称',
      dataIndex: 'username',
      key: 'username',
      render: (v: string) => v || '-',
    },
    {
      title: 'SUPP ID',
      dataIndex: 'userNumber',
      key: 'userNumber',
      render: (v: string | number) => (v != null ? String(v) : '-'),
    },
    {
      title: '用户身份',
      dataIndex: 'vipName',
      key: 'vipName',
      width: 110,
      render: (v: string) => {
        if (!v) return '-';
        return v.replace(/Plus/g, 'Pro');
      },
    },
    {
      title: '点赞数',
      dataIndex: 'likeCount',
      key: 'likeCount',
      width: 90,
      render: (v: number) => v ?? '-',
    },
    {
      title: '评论数',
      dataIndex: 'commentCount',
      key: 'commentCount',
      width: 90,
      render: (v: number) => v ?? '-',
    },
    {
      title: '投稿时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 175,
      render: (v: string) => (v ? dayjs(v).format(FORMAT_TIME) : '-'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: number) => (
        <Tag color={STATUS_MAP[status]?.color ?? 'default'}>
          {STATUS_MAP[status]?.text ?? '未知'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right' as const,
      render: (_: unknown, record: ArtistSpaceArtwork) =>
        record.status === 1 ? (
          <Button
            type="link"
            danger
            onClick={() => handleTakedownClick(record)}
          >
            违规下架
          </Button>
        ) : (
          <Button type="link" onClick={() => handleRelistClick(record)}>
            重新上架
          </Button>
        ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => {
            navigate(PageLink.CommunityTopicMng, {
              state: {
                refreshList: true,
                returnPage: routeState?.fromListPage,
                returnPageSize: routeState?.fromListPageSize,
              },
            });
          }}
            />
            <span>{topicTitle ? `${topicTitle} 投稿作品` : '投稿作品'}</span>
          </Space>
        }
      >
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item name="userNumber" label="用户ID">
            <Input placeholder="请输入用户ID" style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="status" label="作品状态">
            <Select
              placeholder="全部"
              allowClear
              style={{ width: 120 }}
              options={[
                { label: '全部', value: undefined },
                { label: '发布成功', value: 1 },
                { label: '审核未通过', value: 2 },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSearch} style={{ marginRight: 8 }}>
              查询
            </Button>
            <Button onClick={handleReset}>
              重置
            </Button>
          </Form.Item>
        </Form>

        <Table<ArtistSpaceArtwork>
          rowKey={(r) => r.artworkId ?? r.artistSpaceId?.toString() ?? ''}
          columns={columns}
          dataSource={data}
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 条`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Modal
        title="违规下架"
        open={takedownModal.visible}
        onCancel={() => setTakedownModal({ visible: false, record: null })}
        onOk={handleTakedownConfirm}
        okText="确定"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>确定下架作品吗？</p>
        {takedownModal.record?.artworkUrl && (
          <div style={{ marginTop: 8 }}>
            <Image
              src={takedownModal.record.artworkUrl}
              alt="作品"
              width={120}
              style={{ objectFit: 'cover' }}
            />
          </div>
        )}
        {takedownModal.record?.description && (
          <p style={{ marginTop: 8, color: '#666' }}>
            {takedownModal.record.description}
          </p>
        )}
      </Modal>
    </>
  );
};

export default CommunityTopicSubmissionsPage;
